import { EventObject } from '@xstate/store-react';
import { StoreContext } from '../../../models/store/context';
import { StoreEvent } from '../../../enums/storeEvent';
import { SESSION_EXPIRY_MINUTES } from '../../../const/app';
import { store } from '../../store';
import { log } from '@domgen/shared/src/utils/log';

/**
 * Starts or restarts the session expiry timer.
 *
 * Schedules SESSION_EXPIRED dispatch after the configured timeout and returns
 * cleanup logic to clear the timer when the effect is disposed.
 *
 * @param context Current store context.
 * @param event Optional duration override.
 * @param enqueue Store enqueue helper used for timed side effects.
 * @returns Unchanged context.
 */
export const startSessionTimer = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data?: { duration?: number } },
  enqueue: {
    effect: (effect: () => Promise<void> | (() => void) | void) => void;
  },
): StoreContext => {
  log.debug({ message: StoreEvent.START_SESSION_TIMER, data: event.data });
  const duration = event.data?.duration ?? SESSION_EXPIRY_MINUTES * 60 * 1000;

  enqueue.effect(() => {
    const timer = setTimeout(() => {
      store.send({ type: StoreEvent.SESSION_EXPIRED });
    }, duration);

    return () => clearTimeout(timer);
  });

  return context;
};
